import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { Navigate, Routes, Route, useLocation, useNavigate } from "react-router-dom";

import { auth, db, functions } from "./firebase-config.js";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import {
  doc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { stripePromise } from "./stripe-config.js";
import { useActivity } from "./state/ActivityProvider";
import { createUserProfile } from "./services/userService";

import AuthPage from "./AuthPage";
import PublicHome from "./pages/PublicHome";
import BreathingPage from "./pages/BreathingPage.jsx";
import RefugePage from "./pages/RefugePage.jsx";
import "./theme.css";
import { useBackground } from "./hooks/useBackground";
import { applyStoredTheme } from "./hooks/useDarkMode";
import { HelmetProvider } from "react-helmet-async";

import CGV from "./pages/legal/CGV";
import MentionsLegales from "./pages/legal/MentionsLegales";
import PolitiqueConfidentialite from "./pages/legal/PolitiqueConfidentialite";
import Remboursement from "./pages/legal/Remboursement";

import FrequencesPage from "./FrequencesPage";

import UserOnboarding from "./components/UserOnboarding.jsx";

import RessentiPage from "./pages/RessentiPage";
import Apropos from "./pages/apropos";
import Blog from "./pages/blog";
import BlogArticle from "./pages/BlogArticle";
import CalendarPage from "./pages/CalendarPage";
import ProgressPage from "./pages/ProgressPage";
import Guides from "./pages/Guides";
import Resetpassword from "./reset-password.js";
import ForgotPassword from "./ForgotPassword";

import TopbarLeftOnly from "./components/topbar/TopbarLeftOnly";

import PlayerPage from "./pages/PlayerPage";
import ProfilePage from "./pages/ProfilePage";
import Science from "./pages/Science";

import BackgroundLayer from "./components/BackgroundLayer.jsx";
import ScrollToTop from "./components/ScrollToTop";

import { useStreak } from "./hooks/useStreak";
import BadgeCelebration from "./components/BadgeCelebration";

import { tooltipManager } from "./utils/tooltipManager";

import BreathingExportPage from "./pages/BreathingExportPage";

import AIComposerCard from "./components/ai/AIComposerCard.jsx";
import AIPlayerPage from "./pages/AIPlayerPage.jsx";
import AuriaVisualizer from "./pages/AuriaVisualizer";
import AuriaNatureOnboarding from "./components/ai/AuriaNatureOnboarding.jsx";
import {
  clearLegacyAIClientCache,
  subscribePreferences,
} from "./services/ai/memory/aiMemory.js";
import { audioManager } from "./utils/AudioManager";
import { getRecordStreak, readUnlockedRewards, syncStreakRewards } from "./services/rewards/streakRewards.js";
import "./harmonize.css";
import "./components/breathing-flow/BreathingFlow.css";
import "./dark-mode.css";

const soundsDatabase = [
  {
    id: 1,
    name: "Cohérence Cardiaque",
    primaryNeed: "relax",
    description: "Apaise le système nerveux, relâche la pression.",
    breathingPattern: "6 s inspire • 6 s expire (≈5 cycles/minute)",
    adaptHint:
      "Ne vous stressez pas avec les 6 secondes : c'est un repère, pas une règle. Si c'est trop long, inspirez et expirez un peu plus court, à votre aise.",
    explanation:
      "Inspirez doucement pendant 6 secondes par le nez, puis expirez lentement par la bouche pendant 6 secondes. Ce rythme régulier équilibre le système nerveux, apaise le mental et favorise un état de calme profond.",
    frequencies: [{ hz: "888 Hz", type: "Onde Thêta" }],
    files: {
      "5min": {
        music: "/sounds/coherence.mp3",
        noMusic: "/sounds/coherence-ondes.mp3",
        breathing: "/sounds/respirations/coherence-respi.mp3",
      },
    },
  },
  {
    id: 2,
    name: "Carré",
    primaryNeed: "relax",
    description: "Calme et recentre.",
    breathingPattern: "4 s inspire • 4 s pause • 4 s expire • 4 s pause",
    adaptHint:
      "Les 4 temps sont indicatifs. Si tenir chaque durée vous crispe, raccourcissez-les librement : l'important est de garder un rythme régulier, pas de chronométrer.",
    explanation: "Inspirez pendant 4 secondes par le nez, faites une pause pendant 4 secondes, expirez pendant 4 secondes par la bouche, puis faites à nouveau une pause pendant 4 secondes avant de reprendre.",
    frequencies: [{ hz: "417 Hz", type: "Onde Thêta" }],
    files: {
      "5min": {
        music: "/sounds/carre.mp3",
        noMusic: "/sounds/carre-ondes.mp3",
        breathing: "/sounds/respirations/carre-respi.mp3",
      },
    },
  },
  {
    id: 3,
    name: "Soupir Physiologique",
    primaryNeed: "relax",
    description: "Décharge rapide des tensions, détente immédiate.",
    breathingPattern:
      "Grande respiration - petite pause - petite respiration - longue expiration",
    adaptHint:
      "Ne vous focalisez pas sur les durées exactes. Suivez le mouvement à votre façon, plus court si besoin : aucune seconde n'est obligatoire.",
    explanation:
      "Prenez une grande respiration par le nez de 3 secondes suivie d'une petite pause de 1 seconde. Reprenez une petite respiration de 2 secondes par le nez et expirez longuement (environ 8 secondes) par la bouche.",
    frequencies: [{ hz: "396 Hz", type: "Onde Thêta" }],
    files: {
      "5min": {
        music: "/sounds/soupir.mp3",
        noMusic: "/sounds/soupir-ondes.mp3",
        breathing: "/sounds/respirations/soupir-respi.mp3",
      },
    },
  },
  {
    id: 4,
    name: "Longue Expiration",
    primaryNeed: "rest",
    description: "Active le parasympathique, calme instantanément.",
    breathingPattern: "3 s inspire • 7 s expire",
    adaptHint:
      "Si l'expiration de 7 secondes vous semble longue, écourtez-la sans hésiter. Ces durées sont là pour guider, jamais pour vous mettre la pression.",
    explanation:
      "Inspirez pendant 3 secondes par le nez, puis expirez lentement par la bouche (ou le nez) pendant 7 secondes. Relachez les tensions pendant l'expiration.",
    frequencies: [{ hz: "396 Hz", type: "Onde Delta" }],
    files: {
      "5min": {
        music: "/sounds/longue-exp.mp3",
        noMusic: "/sounds/longue-exp-ondes.mp3",
        breathing: "/sounds/respirations/longue-exp-respi.mp3",
      },
    },
  },
  {
    id: 5,
    name: "4-7-8",
    primaryNeed: "rest",
    description: "Guide vers un endormissement naturel et paisible.",
    breathingPattern: "4 s inspire • 7 s pause • 8 s expire",
    adaptHint:
      "Ne vous inquiétez pas si vous ne tenez pas le 7 ou le 8 : ce sont des repères. Réduisez chaque temps autant que nécessaire, l'exercice reste efficace.",
    explanation: "Inspirez par le nez pendant 4 secondes, faites une pause pendant 7 secondes, puis expirez lentement par la bouche pendant 8 secondes. Cette méthode aide à ralentir progressivement le rythme et à accompagner le corps vers le repos.",
    frequencies: [{ hz: "174 Hz", type: "Onde Delta" }],
    files: {
      "5min": {
        music: "/sounds/4-7-8.mp3",
        noMusic: "/sounds/4-7-8-ondes.mp3",
        breathing: "/sounds/respirations/4-7-8-respi.mp3",
      },
    },
  },
  {
    id: 6,
    name: "Respiration Alternée",
    primaryNeed: "rest",
    description: "Équilibre le mental et apaise avant le sommeil.",
    breathingPattern: "3 s inspire • 3 s expire • alternance nez gauche/droite",
    adaptHint:
      "Les durées ne sont qu'une indication. Allez plus vite ou plus lentement selon votre confort : seule l'alternance calme entre les narines compte vraiment.",
    explanation:
      "La respiration alternée, aussi appelée Nadi Shodhana, consiste à inspirer par une narine puis expirer par l’autre, en alternant doucement les côtés. Inspirez par la gauche, expirez par la droite, puis inspirez par la droite et expirez par la gauche. Ce rythme régulier calme l’esprit, favorise l’équilibre intérieur et prépare progressivement au repos.",
    frequencies: [{ hz: "417 Hz", type: "Onde Delta" }],
    files: {
      "5min": {
        music: "/sounds/nadi.mp3",
        noMusic: "/sounds/nadi-ondes.mp3",
        breathing: "/sounds/respirations/nadi-respi.mp3",
      },
    },
  },
  {
    id: 7,
    name: "Respiration Triangle",
    primaryNeed: "recenter",
    description: "Améliore la concentration et la clarté mentale",
    breathingPattern: "4 s inspire • 4 s pause • longue expiration",
    adaptHint:
      "Si la longue expiration vous paraît interminable, raccourcissez-la sans culpabiliser. Ces durées sont un guide, pas une performance à atteindre.",
    explanation:
      "Inspirez profondément pendant environ 4 secondes par le nez, faites une pause pendant environ 4 secondes, expirez lentement avec la bouche presque fermé pendant environ 8 secondes, jusqu'à vider complétement les poumons.",
    frequencies: [{ hz: "741 Hz", type: "Onde Alpha" }],
    files: {
      "5min": {
        music: "/sounds/triangle.mp3",
        noMusic: "/sounds/triangle-ondes.mp3",
        breathing: "/sounds/respirations/triangle-respi.mp3",
      },
    },
  },
  {
    id: 8,
    name: "Boosteur d'énergie",
    primaryNeed: "recenter",
    description: "Éveille le corps et clarifie l'esprit en quelques secondes",
    breathingPattern: "inspiration et expiration rapide • 10 s normal",
    adaptHint:
      "Pas besoin de coller exactement aux 10 secondes. Ralentissez ou écourtez si nécessaire : ces durées sont indicatives, écoutez votre corps avant tout.",
    explanation:
      "Inspirez et expirez rapidement par le nez (bouche toujours fermée). Tenez ce rythme 10 secondes. Faites une pause : respiration normale pendant 10 secondes. Recommencez.",
    frequencies: [{ hz: "741 Hz", type: "Onde Alpha" }],
    files: {
      "5min": {
        music: "/sounds/boosteur.mp3",
        noMusic: "/sounds/boosteur-ondes.mp3",
        breathing: "/sounds/respirations/boosteur-respi.mp3",
      },
    },
  },
  {
    id: 9,
    name: "Grande Ventilation",
    primaryNeed: "recenter",
    description:
      "Oxygène le corps, ouvre la cage thoracique, dynamise en douceur.",
    breathingPattern: "8 s inspire en 3 paliers • 8 s expire en 3 paliers",
    adaptHint:
      "Ne vous stressez pas avec les 8 secondes par paliers. Faites-les plus courts si besoin : ce sont des repères pour vous guider, rien de plus.",
    explanation:
      "Inspirez lentement par le nez en remplissant successivement le ventre, la poitrine puis les clavicules, puis expirez par la bouche dans l'ordre inverse en vidant d'abord les clavicules, la poitrine puis le ventre.",
    frequencies: [{ hz: "256 Hz", type: "Onde Alpha" }],
    files: {
      "5min": {
        music: "/sounds/grande-ventil.mp3",
        noMusic: "/sounds/grande-ventil-ondes.mp3",
        breathing: "/sounds/respirations/grande-ventil-respi.mp3",
      },
    },
  },
  {
    id: 10,
    name: "Oxygénation de l’organisme",
    primaryNeed: "health",
    description: "Active la circulation lymphatique et purifie l'organisme.",
    breathingPattern: "4 s inspire • 16 s pause • 8 s expire",
    adaptHint:
      "La pause de 16 secondes est volontairement longue : ne forcez surtout pas. Ces durées sont des repères, raccourcissez-les autant qu'il faut.",
    explanation:
      "Inspirez profondément par le nez pendant 4 secondes en partant du bas de l'abdomen, faites une pause pendant 16 secondes, puis expirez lentement par la bouche pendant 8 secondes. Cette respiration agit comme un aspirateur naturel : elle active la circulation lymphatique, oxygène le sang et aide l'organisme à éliminer les toxines. Pratiquée régulièrement, elle soutient le système immunitaire et améliore la santé globale.",
    frequencies: [{ hz: "396 Hz", type: "Onde Thêta" }],
    isBonus: true,
    files: {
      "5min": {
        music: "/sounds/oxy.mp3",
        noMusic: "/sounds/oxy-ondes.mp3",
        breathing: "/sounds/respirations/oxy-respi.mp3",
      },
    },
  },
];

export const NATURAL_SOUNDS = Object.freeze([
  {
    id: "bruit",
    name: "Bruit Blanc",
    image: "/illustrations/12.svg",
    file: "/sounds/natural/bruit-blanc.mp3",
    premium: false,
  },
  {
    id: "oiseaux",
    name: "Oiseaux",
    image: "/illustrations/13.svg",
    file: "/sounds/natural/oiseaux.mp3",
    premium: false,
  },
  {
    id: "ruisseau",
    name: "Ruisseau",
    image: "/illustrations/14.svg",
    file: "/sounds/natural/ruisseau.mp3",
    premium: false,
  },
  {
    id: "mer",
    name: "Vagues",
    image: "/illustrations/15.svg",
    file: "/sounds/natural/mer.mp3",
    premium: false,
  },
  {
    id: "feu",
    name: "Feu crépitant",
    image: "/illustrations/16.svg",
    file: "/sounds/natural/feu.mp3",
    premium: false,
  },
  {
    id: "pluie",
    name: "Pluie",
    image: "/illustrations/17.svg",
    file: "/sounds/natural/pluie.mp3",
    premium: false,
  },
  {
    id: "tonnerre",
    name: "Tonnerre",
    image: "/illustrations/18.svg",
    file: "/sounds/natural/tonnerre.mp3",
    premium: false,
  },
  {
    id: "criquets",
    name: "Criquets",
    image: "/illustrations/19.svg",
    file: "/sounds/natural/crickets.mp3",
    premium: false,
  },
  {
    id: "train",
    name: "Train",
    image: "/illustrations/26.svg",
    file: "/sounds/natural/train.mp3",
    premium: true,
  },
  {
    id: "bol",
    name: "Bols tibétains",
    image: "/illustrations/21.svg",
    file: "/sounds/natural/bol.mp3",
    premium: true,
  },
  {
    id: "mouette",
    name: "Mouettes et Océan",
    image: "/illustrations/25.svg",
    file: "/sounds/natural/mouette.mp3",
    premium: true,
  },
  {
    id: "fan",
    name: "Chauffage électrique",
    image: "/illustrations/27.svg",
    file: "/sounds/natural/fan.mp3",
    premium: true,
  },
  {
    id: "toundra",
    name: "Toundra arctique",
    image: "/illustrations/22.svg",
    file: "/sounds/natural/toundra.mp3",
    premium: true,
  },
  {
    id: "baleine",
    name: "Chant des baleines",
    image: "/illustrations/20.svg",
    file: "/sounds/natural/baleine.mp3",
    premium: true,
  },
  {
    id: "owl",
    name: "Forêt Nocturne",
    image: "/illustrations/24.svg",
    file: "/sounds/natural/owl.mp3",
    premium: false,
    streakDays: 60,
  },
  {
    id: "chat",
    name: "Ronronnement",
    image: "/illustrations/23.svg",
    file: "/sounds/natural/ronro.mp3",
    premium: true,
  },
  {
    id: "grotte",
    name: "Grotte",
    image: "/illustrations/28.svg",
    file: "/sounds/natural/cave.mp3",
    premium: false,
    streakDays: 60,
  },
  {
    id: "abysse",
    name: "Fond marin",
    image: "/illustrations/29.svg",
    file: "/sounds/natural/underwater.mp3",
    premium: true,
  },
]);

const PUBLIC_DEMO_NATURAL_SOUNDS = Object.freeze(NATURAL_SOUNDS.filter((sound) => !sound.premium && !sound.streakDays));

function sortNaturalSoundsByFavorites(sounds, favoriteIds, isPremium) {
  if (!Array.isArray(favoriteIds) || favoriteIds.length === 0) {
    return sounds;
  }

  const favSet = new Set(favoriteIds);
  const favs = [];
  const rest = [];

  sounds.forEach((s) => {
    // Un favori ne remonte que s'il est accessible :
    // - soit l'utilisateur est premium
    // - soit le son n'est pas premium
    const isAccessibleFav = favSet.has(s.id) && (isPremium || !s.premium);

    if (isAccessibleFav) favs.push(s);
    else rest.push(s);
  });

  favs.sort((a, b) => favoriteIds.indexOf(a.id) - favoriteIds.indexOf(b.id));

  return [...favs, ...rest];
}

function safeGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    console.warn("localStorage inaccessible.");
    return null;
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, value);

    // ✅ Force les listeners (useStreak) à réagir immédiatement dans le même onglet
    window.dispatchEvent(new Event("storage"));
  } catch {
    console.warn("Impossible d’écrire dans localStorage.");
  }
}

function cleanPseudoForProfile(value = "") {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, 12);
}

function getHomeReturnGreeting(user, profilePseudo = "", date = new Date()) {
  const rawName =
    user?.pseudo ||
    user?.displayName ||
    profilePseudo ||
    user?.email?.split("@")[0] ||
    "";

  const normalizedName =
    String(rawName)
      .trim()
      .replace(/[._-]+/g, " ")
      .split(/\s+/)
      .filter(Boolean)[0] || "";

  const name = normalizedName
    ? normalizedName.charAt(0).toUpperCase() + normalizedName.slice(1)
    : "";

  const hour = date.getHours();
  const greeting = hour >= 17 || hour < 5 ? "Bonsoir" : "Bonjour";

  return name ? `${greeting} ${name}` : greeting;
}

// YYYY-MM-DD dans le fuseau Europe/Paris
function toISOParis(date = new Date()) {
  const f = new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const [dd, mm, yyyy] = f.format(date).split("/");
  return `${yyyy}-${mm}-${dd}`;
}

function mondayISOParis(date = new Date()) {
  const [y, m, d] = toISOParis(date).split("-").map(Number);
  const local = new Date(y, m - 1, d, 12);
  local.setDate(local.getDate() - ((local.getDay() + 6) % 7));
  return `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(2, "0")}-${String(local.getDate()).padStart(2, "0")}`;
}

function formatDurationLabelFromMinutes(min) {
  if (min == null || isNaN(min)) return "";
  const totalSeconds = Math.round(min * 60);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0) return `${seconds} sec`;
  if (seconds === 0) return `${minutes} min`;
  return `${minutes} min ${seconds} sec`;
}

const STRIPE_PRICE_MONTHLY = "price_1TWG5EGirb6zLStUnY8LnBMF"; // 6,99€/mois
const STRIPE_PRICE_YEARLY = "price_1TWG67Girb6zLStUQwLwe7V8"; // 59,88€/an

const CANONICAL_ORIGIN = "https://harmonauria.fr";
const LEGACY_HOSTS = new Set([
  "harmonauria-music.web.app",
  "harmonauria-music.firebaseapp.com",
]);

function getCanonicalOrigin() {
  return LEGACY_HOSTS.has(window.location.hostname)
    ? CANONICAL_ORIGIN
    : window.location.origin;
}

function isPublicPath(path) {
  return (
    path === "/" ||
    path === "/moment" ||
    path === "/respirer" ||
    path.startsWith("/auth/") ||
    path.startsWith("/legal/") ||
    path === "/blog" ||
    path.startsWith("/blog/") ||
    path === "/apropos" ||
    path === "/frequences" ||
    path === "/science" ||
    path === "/reset-password" ||
    path === "/forgot-password"
  );
}

const DAILY_GOAL_BY_TIME = { "30sec": 0.5, "2min": 2, "5min": 5, "10min": 10 };
const DEFAULT_DAILY_GOAL = 2;
const CLASSIC_DURATIONS = new Set(["30sec", "2min", "5min"]);

const FREE_MINUTES_LIMIT = 10; // minutes d'écoute cumulées max
const FREE_COOLDOWN_MS = 2 * 60 * 60 * 1000; // 2 h en ms

const PREMIUM_IDS_BASE = [3, 6, 9];
const AURIA_ORIGIN_OPTICAL_Y = 0.04; // remonte l'origine de 4 % de la hauteur d'Auria
const HOME_INTRO_VISIBLE_MS = 1800;
const HOME_INTRO_LEAVE_MS = 600;

function homeIntroLastShownKey(uid) {
  return `homeIntro:lastShown:${uid}`;
}

function readHomeIntroTimestamp(key) {
  try {
    const value = Number(localStorage.getItem(key) || 0);
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

function writeHomeIntroTimestamp(key, value = Date.now()) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* noop */
  }
}
function isFirstGreetingOfDay(lastShownAt, now = Date.now()) {
  if (!lastShownAt) return true;
  return toISOParis(new Date(lastShownAt)) !== toISOParis(new Date(now));
}

function freeLimitKey(uid) {
  return `free:limit:${uid}`;
}

function readFreeLimit(uid) {
  try {
    const raw = safeGet(freeLimitKey(uid));
    if (!raw) return { minutes: 0, cooldownUntil: 0 };
    const parsed = JSON.parse(raw);
    const minutes = Number(parsed.minutes) || 0;
    const cooldownUntil = Number(parsed.cooldownUntil) || 0;
    if (cooldownUntil > 0 && Date.now() >= cooldownUntil) return { minutes: 0, cooldownUntil: 0 };
    return { minutes, cooldownUntil };
  } catch {
    return { minutes: 0, cooldownUntil: 0 };
  }
}

function writeFreeLimit(uid, data) {
  safeSet(freeLimitKey(uid), JSON.stringify(data));
}

function isInCooldown(uid) {
  const { cooldownUntil } = readFreeLimit(uid);
  return Date.now() < cooldownUntil;
}

function getCooldownRemaining(uid) {
  const { cooldownUntil } = readFreeLimit(uid);
  return Math.max(0, cooldownUntil - Date.now());
}

function getDailyGoalFromTime(choice) {
  return DAILY_GOAL_BY_TIME[choice] ?? DEFAULT_DAILY_GOAL;
}

function currentTrackIdForLog(currentSound, selectedDuration, withMusic) {
  const base = currentSound?.id ?? "unknown";
  const dur = selectedDuration || "na";
  const mode = withMusic ? "music" : "noMusic";
  return `${base}:${dur}:${mode}`;
}

function todayKey(uid) {
  const iso = toISOParis(new Date());
  return `pg:${uid}:day:${iso}`;
}
function weekKey(uid) {
  const d = new Date();
  const y = d.getUTCFullYear();
  const onejan = new Date(Date.UTC(y, 0, 1));
  const week = Math.ceil(
    ((d - onejan) / 86400000 + onejan.getUTCDay() + 1) / 7,
  );
  return `pg:${uid}:week:${y}-W${week}`;
}
function readProgress(uid) {
  try {
    const day = JSON.parse(safeGet(todayKey(uid)) || "{}");
    const wk = JSON.parse(safeGet(weekKey(uid)) || "{}");
    return {
      dayMin: Number(day.min) || 0,
      daySessions: Number(day.s) || 0,
      weekMin: Number(wk.min) || 0,
      weekSessions: Number(wk.s) || 0,
    };
  } catch {
    return { dayMin: 0, daySessions: 0, weekMin: 0, weekSessions: 0 };
  }
}
function writeProgress(uid, data) {
  try {
    const dKey = todayKey(uid),
      wKey = weekKey(uid);
    const cur = readProgress(uid);

    const day = {
      min: data.dayMin ?? cur.dayMin,
      s: data.daySessions ?? cur.daySessions,
    };
    const wk = {
      min: data.weekMin ?? cur.weekMin,
      s: data.weekSessions ?? cur.weekSessions,
    };

    safeSet(dKey, JSON.stringify(day));
    safeSet(wKey, JSON.stringify(wk));
  } catch (err) {
    console.warn("Écriture progression impossible:", err);
  }
}

function bumpSessionCount(uid) {
  if (!uid) return;
  try {
    const key = `sessions:total:${uid}`;
    localStorage.setItem(key, String((Number(localStorage.getItem(key)) || 0) + 1));
  } catch { /* noop */ }
}

function dailyCongratsKey(uid) {
  return `pg:${uid}:dailyCongrats:${toISOParis(new Date())}`;
}

function hasSeenDailyCongrats(uid) {
  return safeGet(dailyCongratsKey(uid)) === "1";
}

function markDailyCongratsSeen(uid) {
  safeSet(dailyCongratsKey(uid), "1");
}

function useLockBodyScroll(locked) {
  useEffect(() => {
    if (!locked) return;

    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";

    return () => {
      html.style.overflow = "";
      body.style.overflow = "";
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      window.scrollTo(0, scrollY);
    };
  }, [locked]);
}

function FreeLimitModal({ uid, onClose }) {
  const [remaining, setRemaining] = useState(() => getCooldownRemaining(uid));

  useEffect(() => {
    if (remaining <= 0) {
      onClose();
      return;
    }

    const id = setInterval(() => {
      const r = getCooldownRemaining(uid);
      setRemaining(r);
      if (r <= 0) {
        clearInterval(id);
        onClose();
      }
    }, 1000);

    return () => clearInterval(id);
  }, [uid, onClose, remaining]);

  const h = Math.floor(remaining / 3_600_000);
  const m = Math.floor((remaining % 3_600_000) / 60_000);
  const s = Math.floor((remaining % 60_000) / 1_000);
  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div
      className="free-limit-overlay"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 16,
        background: "var(--free-limit-overlay-bg)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        className="free-limit-card"
        style={{
          width: "min(400px, 90vw)",
          borderRadius: 24,
          padding: "32px 24px 28px",
          background: "var(--free-limit-card-bg)",
          border: "1px solid var(--free-limit-card-border)",
          boxShadow: "var(--free-limit-card-shadow)",
          textAlign: "center",
          color: "var(--free-limit-text)",
          animation: "premiumPop .22s ease-out both",
        }}
      >
        <div style={{ fontSize: 48, marginBottom: 12 }}>⏳</div>

        <h2
          style={{
            margin: "0 0 10px",
            fontSize: 22,
            fontWeight: 900,
            letterSpacing: "-0.03em",
            color: "var(--free-limit-title)",
          }}
        >
          Revenez dans un moment
        </h2>

        <p
          style={{
            margin: "0 0 24px",
            fontSize: 14,
            lineHeight: 1.55,
            color: "var(--free-limit-muted)",
          }}
        >
Vous avez atteint la limite d'écoute gratuite ({FREE_MINUTES_LIMIT} minutes).
          <br />
          L'accès se rétablit automatiquement après un temps de pause.
        </p>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 8,
            marginBottom: 24,
          }}
        >
          {[
            { val: pad(h), label: "heure" + (h !== 1 ? "s" : "") },
            { val: pad(m), label: "min" },
            { val: pad(s), label: "sec" },
          ].map(({ val, label }) => (
            <div
              key={label}
              style={{
                background: "var(--free-limit-count-bg)",
                border: "1px solid var(--free-limit-count-border)",
                borderRadius: 14,
                padding: "10px 14px",
                minWidth: 58,
              }}
            >
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 900,
                  lineHeight: 1,
                  fontVariantNumeric: "tabular-nums",
                  color: "var(--free-limit-count-text)",
                }}
              >
                {val}
              </div>
              <div
                style={{
                  fontSize: 11,
                  marginTop: 4,
                  color: "var(--free-limit-count-label)",
                  fontWeight: 600,
                }}
              >
                {label}
              </div>
            </div>
          ))}
        </div>

        <p
          style={{
            margin: "0 0 16px",
            fontSize: 13,
            color: "var(--free-limit-soft)",
          }}
        >
          Sans limite avec le Refuge Personnel 🌿
        </p>

        <button
          onClick={onClose}
          style={{
            width: "100%",
            border: "none",
            borderRadius: 14,
            padding: "14px 18px",
            fontSize: 14,
            fontWeight: 800,
            cursor: "pointer",
            background: "var(--free-limit-button-bg)",
            color: "var(--free-limit-button-text)",
          }}
        >
          Fermer
        </button>
<style>{`
  :root {
    --free-limit-overlay-bg: rgba(15, 23, 42, 0.22);
    --free-limit-card-bg: linear-gradient(160deg, #ffffff 0%, #f8fbfa 100%);
    --free-limit-card-border: rgba(97, 131, 122, 0.2);
    --free-limit-card-shadow: 0 24px 60px rgba(49, 72, 66, 0.16);
    --free-limit-text: #1f2937;
    --free-limit-title: #4f756d;
    --free-limit-muted: #4b5563;
    --free-limit-soft: #6b7280;
    --free-limit-count-bg: rgba(203, 226, 219, 0.3);
    --free-limit-count-border: rgba(97, 131, 122, 0.2);
    --free-limit-count-text: #203e3e;
    --free-limit-count-label: #4f756d;
    --free-limit-button-bg: rgba(15, 23, 42, 0.06);
    --free-limit-button-text: #4b5563;
  }
`}</style>
      </div>
    </div>
  );
}

function DailyGoalCongratsToast({ goalLabel, onClose }) {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="daily-celebration"
      role="dialog"
      aria-modal="true"
      aria-labelledby="daily-celebration-title"
      aria-describedby="daily-celebration-description"
    >
      <div
        className="daily-celebration__sparkles"
        aria-hidden="true"
      >
        <span>✦</span>
        <span>✧</span>
        <span>✦</span>
        <span>•</span>
        <span>✧</span>
        <span>✦</span>
        <span>•</span>
        <span>✧</span>
      </div>

      <div className="daily-celebration__content">
        <div
          className="daily-celebration__icon"
          aria-hidden="true"
        >
          <span>✓</span>
        </div>

        <p className="daily-celebration__eyebrow">
          Un moment pour vous
        </p>

        <h2
          id="daily-celebration-title"
          className="daily-celebration__title"
        >
          Votre objectif du jour
          <br />
          est atteint
        </h2>

        <p
          id="daily-celebration-description"
          className="daily-celebration__description"
        >
          Aujourd’hui, vous avez pris{" "}
          <strong>{goalLabel}</strong> pour respirer et retrouver
          votre calme.
          <br />
          Ce moment compte.
        </p>

        <button
          type="button"
          className="daily-celebration__button"
          onClick={onClose}
          autoFocus
        >
          Revenir à mon refuge
        </button>
      </div>
    </div>
  );
}

function consumeFreeMinutes(uid, minutes) {
  if (!uid || !(minutes > 0)) return;

  const limit = readFreeLimit(uid);
  const newMinutes = (limit.minutes || 0) + minutes;

  writeFreeLimit(uid, { minutes: newMinutes, cooldownUntil: newMinutes >= FREE_MINUTES_LIMIT ? Date.now() + FREE_COOLDOWN_MS : limit.cooldownUntil });
}

const VALID_PRIMARY_NEEDS = new Set([
  "relax",
  "rest",
  "recenter",
]);

const VALID_DAILY_TIMES = new Set([
  "30sec",
  "2min",
  "5min",
  "10min",
]);

function normalizeOnboardingProfile(profile = {}) {
  const primaryNeed = VALID_PRIMARY_NEEDS.has(
    profile.primaryNeed,
  )
    ? profile.primaryNeed
    : "";

  const dailyTime = VALID_DAILY_TIMES.has(
    profile.dailyTime,
  )
    ? profile.dailyTime
    : "2min";

  return {
    primaryNeed,
    dailyTime,
    onboardingVersion: 3,
  };
}

function resolveOnboardingState(data = {}, legacyProfile = null) {
  const remote = normalizeOnboardingProfile(data.onboarding || {});
  const legacy = normalizeOnboardingProfile(legacyProfile || {});
  const completed = data.onboardingStatus === "completed" || Boolean(data.onboarding?.completedAt || data.onboarding?.migratedAt || remote.primaryNeed || legacy.primaryNeed);
  const profile = remote.primaryNeed ? remote : legacy.primaryNeed ? legacy : { primaryNeed: "relax", dailyTime: remote.dailyTime, onboardingVersion: 3 };
  return { profile, required: !completed && data.onboardingStatus === "pending" };
}

function BodyPortal({ children }) {
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
}

function PremiumRoute({ user, status, isPremium, onRetry, children }) {
  if (!user) return <Navigate to="/auth/login" replace />;
  if (status === "checking") return <div className="app-fill" role="status" aria-live="polite" style={{ display: "grid", placeItems: "center" }}>Vérification de votre accès…</div>;
  if (status === "error") return <div className="app-fill" role="alert" style={{ display: "grid", placeItems: "center", textAlign: "center", padding: 24 }}><div><p>Votre accès ne peut pas être vérifié pour le moment.</p><button type="button" onClick={onRetry}>Réessayer</button></div></div>;
  if (!isPremium) return <Navigate to="/" replace />;
  return children;
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const cleanupRef = useRef(null);

  useEffect(() => {
    clearLegacyAIClientCache();
  }, []);

  useEffect(() => {
    if (LEGACY_HOSTS.has(window.location.hostname)) {
      const target =
        CANONICAL_ORIGIN +
        window.location.pathname +
        window.location.search +
        window.location.hash;

      window.location.replace(target);
    }
  }, []);

const isLegalPage = location.pathname.startsWith("/legal/");
const isBlogArticlePage =
  location.pathname.startsWith("/blog/") && location.pathname !== "/blog";
  const [currentStep, setCurrentStep] = useState("welcome");
  const [homeReturnIntroPhase, setHomeReturnIntroPhase] = useState("idle");
  const [homeReturnIntroToken, setHomeReturnIntroToken] = useState(0);
  const homeReturnIntroCheckedUidRef = useRef(null);
  const homeReturnIntroDeadlineRef = useRef(0);

  const navigateStep = useCallback(
    (step) => {
      setCurrentStep(step);

      const params = new URLSearchParams(window.location.search);
      if (step === "welcome") params.delete("step");
      else params.set("step", step);

      navigate({
        pathname: "/",
        search: params.toString() ? `?${params.toString()}` : "",
      });
    },
    [navigate],
  );

  // Onboarding / profil
  const [onboardingProfile, setOnboardingProfile] =
    useState({
    primaryNeed: "",
    dailyTime: "",
    onboardingVersion: 3,
  });

  const [showOnboarding, setShowOnboarding] = useState(false);

const [currentSound, setCurrentSound] = useState(null);

const [withMusic, setWithMusic] = useState(true);

const [selectedDuration, setSelectedDuration] =
  useState("");

const [playerMainSrc, setPlayerMainSrc] =
  useState("");

const [playerBreathingSrc, setPlayerBreathingSrc] =
  useState("");

const [publicDemoIntent, setPublicDemoIntent] = useState(null);
const isPublicDemo = Boolean(publicDemoIntent);

const [playerTransitionVisible, setPlayerTransitionVisible] = useState(false);
const playerTransitionTimerRef = useRef(null);

useEffect(() => {
  const showPlayerTransition = () => {
    window.clearTimeout(playerTransitionTimerRef.current);
    setPlayerTransitionVisible(true);
    playerTransitionTimerRef.current = window.setTimeout(() => {
      setPlayerTransitionVisible(false);
      playerTransitionTimerRef.current = null;
    }, 2200);
  };

  window.addEventListener("harmonauria:player-transition-start", showPlayerTransition);

  return () => {
    window.removeEventListener("harmonauria:player-transition-start", showPlayerTransition);
    window.clearTimeout(playerTransitionTimerRef.current);
  };
}, []);

useLayoutEffect(() => {
  if (!playerTransitionVisible) return undefined;

  const destinationMounted = currentStep === "player" || location.pathname === "/ia/player";
  if (!destinationMounted) return undefined;

  let secondFrame = 0;
  const firstFrame = window.requestAnimationFrame(() => {
    secondFrame = window.requestAnimationFrame(() => {
      window.clearTimeout(playerTransitionTimerRef.current);
      playerTransitionTimerRef.current = null;
      setPlayerTransitionVisible(false);
    });
  });

  return () => {
    window.cancelAnimationFrame(firstFrame);
    if (secondFrame) window.cancelAnimationFrame(secondFrame);
  };
}, [currentStep, location.pathname, playerTransitionVisible]);

  // Auth
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [premiumStatus, setPremiumStatus] = useState("checking");
  const [profileHydrated, setProfileHydrated] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileRetry, setProfileRetry] = useState(0);
  const signupIntentRef = useRef(false);
  const [pseudo, setPseudo] = useState("");

  const { bgIndex, imageFiles, desktopImageFiles } = useBackground(isPremium);

  useEffect(() => {
  if (!user?.uid) {
    setWithMusic(true);
    return;
  }

  setWithMusic(user?.uiPreferences?.playerListeningMode !== "noMusic");
}, [user?.uid, user?.uiPreferences?.playerListeningMode]);

  // Progression UI
  const [pgDayMin, setPgDayMin] = useState(0);
  const [pgWeekMin, setPgWeekMin] = useState(0);
  const [progressReveal, setProgressReveal] = useState(null);

const [showDailyCongrats, setShowDailyCongrats] = useState(false);
const dailyCongratsPendingRef = useRef(false);
const progressRevealActiveRef = useRef(false);

  // Stripe / Premium modal
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  const [premiumBilling, setPremiumBilling] = useState("monthly");

useLockBodyScroll(
  showPremiumModal ||
    showOnboarding ||
    showDailyCongrats,
);

  const [showFreeLimitModal, setShowFreeLimitModal] = useState(false);

  const [newsletterAccepted, setNewsletterAccepted] = useState(false);

  // Safe mounted
  const isMountedRef = useRef(true);

  const firestoreUnsubRef = useRef(null);
  const authEpochRef = useRef(0);
  const [premiumActivating, setPremiumActivating] = useState(false);
  const premiumRefreshAttemptedRef = useRef(null);

  // Activity logging
  const { addEntry, activity, isHydrating: isActivityHydrating } = useActivity();
  const sessionLoggedRef = useRef(false);

  const pendingNewsletterRef = useRef(null);

    useEffect(() => {
    if (!user?.uid || isActivityHydrating) return;

    const todayISO = toISOParis(new Date());
    const weekStartISO = mondayISOParis(new Date());
    let dayFromActivity = 0;
    let weekFromActivity = 0;

    (Array.isArray(activity) ? activity : []).forEach((entry) => {
      const minutes = Math.max(0, Number(entry?.minutes) || 0);
      if (!minutes || !entry?.dateISO) return;
      if (entry.dateISO === todayISO) dayFromActivity += minutes;
      if (entry.dateISO >= weekStartISO && entry.dateISO <= todayISO) weekFromActivity += minutes;
    });

    const cur = readProgress(user.uid);
    const nextDay = Math.max(cur.dayMin, dayFromActivity);
    const nextWeek = Math.max(cur.weekMin, weekFromActivity);

    if (nextDay !== cur.dayMin || nextWeek !== cur.weekMin) {
      writeProgress(user.uid, { dayMin: nextDay, weekMin: nextWeek });
    }

    setPgDayMin((prev) => (prev >= nextDay ? prev : nextDay));
    setPgWeekMin((prev) => (prev >= nextWeek ? prev : nextWeek));
  }, [activity, isActivityHydrating, user?.uid]);

const effectiveDailyTime = onboardingProfile?.dailyTime || user?.onboarding?.dailyTime || "2min";
  const dailyGoal = getDailyGoalFromTime(effectiveDailyTime);

const connectedHomeActive = Boolean(
  user && profileHydrated && !profileError && !isLoading &&
    !showOnboarding &&
    location.pathname === "/" &&
    currentStep === "welcome",
);

  const effectivePrimaryNeed = VALID_PRIMARY_NEEDS.has(
    onboardingProfile?.primaryNeed,
  )
    ? onboardingProfile.primaryNeed
    : "relax";

  const trialEligible = !user?.trialUsed;


const [auriaPreferences, setAuriaPreferences] = useState(null);
const [showAuriaNatureOnboarding, setShowAuriaNatureOnboarding] =
  useState(false);

useEffect(() => {
  if (!user?.uid || !isPremium || showOnboarding) {
    setAuriaPreferences(null);
    setShowAuriaNatureOnboarding(false);
    return undefined;
  }
  return subscribePreferences(user.uid, (preferences) => {
    setAuriaPreferences(preferences);
    const favorites = preferences.favoriteNatureSoundIds || [];
    setShowAuriaNatureOnboarding(!preferences.onboarding?.natureCompletedAt || favorites.length !== 3);
  }, (error) => console.warn("Auria preferences loading failed.", error));
}, [user?.uid, isPremium, showOnboarding]);

  const favoriteNatureSoundIds = useMemo(
    () => auriaPreferences?.favoriteNatureSoundIds || [],
    [auriaPreferences?.favoriteNatureSoundIds],
  );

  const orderedNaturalSounds = useMemo(
    () =>
      sortNaturalSoundsByFavorites(
        NATURAL_SOUNDS,
        favoriteNatureSoundIds,
        isPremium,
      ),
    [favoriteNatureSoundIds, isPremium],
  );


  const returnToHomeAfterPlayer = useCallback(
  () => {
    setCurrentSound(null);
    setPlayerMainSrc("");
    setPlayerBreathingSrc("");
    setSelectedDuration("");

    navigateStep("welcome");
  },
  [navigateStep],
);

const exitPublicDemo = useCallback(() => {
  setPublicDemoIntent(null);
  setCurrentSound(null);
  setPlayerMainSrc("");
  setPlayerBreathingSrc("");
  setSelectedDuration("");
  navigateStep("welcome");
}, [navigateStep]);

const startPublicDemo = useCallback(({ intent = "free", billing = "monthly" } = {}) => {
  const demoSound = soundsDatabase.find((sound) => sound.id === 1);
  const demoFiles = demoSound?.files?.["5min"];

  if (!demoSound || !demoFiles?.music) return;

  const normalizedIntent = intent === "premium" ? "premium" : "free";
  const normalizedBilling = billing === "yearly" ? "yearly" : "monthly";

  try {
    localStorage.removeItem("directCheckout");
    localStorage.removeItem("showPremiumAfterAuth");

    if (normalizedIntent === "premium") {
      localStorage.setItem("preferredBilling", normalizedBilling);
    }
  } catch {
    /* noop */
  }

  setPublicDemoIntent({
    intent: normalizedIntent,
    billing: normalizedBilling,
  });

  setSelectedDuration("48sec");
  setCurrentSound(demoSound);
  setPlayerMainSrc(demoFiles.music);
  setPlayerBreathingSrc(demoFiles.breathing || "");
  navigateStep("player");
}, [navigateStep]);

const openPublicAuth = useCallback((mode) => {
  try {
    localStorage.removeItem("directCheckout");
    localStorage.removeItem("showPremiumAfterAuth");
  } catch {
    /* noop */
  }

  setPublicDemoIntent(null);
  navigate(`/auth/${mode}`);
}, [navigate]);

const startPremiumSignup = useCallback((billing) => {
  const normalizedBilling = billing === "yearly" ? "yearly" : "monthly";

  try {
    localStorage.removeItem("directCheckout");
    localStorage.setItem("showPremiumAfterAuth", "true");
    localStorage.setItem("preferredBilling", normalizedBilling);
  } catch {
    /* noop */
  }

  setPublicDemoIntent(null);
  navigate("/auth/signup", { state: { from: "public-premium" } });
}, [navigate]);

const continueAfterPublicDemo = useCallback((mode) => {
  const wantsPremium = publicDemoIntent?.intent === "premium";
  const billing = publicDemoIntent?.billing === "yearly" ? "yearly" : "monthly";

  try {
    localStorage.removeItem("directCheckout");
    localStorage.removeItem("pendingSession");

    if (wantsPremium) {
      localStorage.setItem("showPremiumAfterAuth", "true");
      localStorage.setItem("preferredBilling", billing);
    } else {
      localStorage.removeItem("showPremiumAfterAuth");
    }
  } catch {
    /* noop */
  }

  setPublicDemoIntent(null);
  setCurrentSound(null);
  setPlayerMainSrc("");
  setPlayerBreathingSrc("");
  setSelectedDuration("");
  navigate(`/auth/${mode}`, {
    state: {
      from: "public-demo",
    },
  });
}, [navigate, publicDemoIntent]);

useEffect(() => {
  dailyCongratsPendingRef.current = false;
  setShowDailyCongrats(false);
}, [user?.uid]);

useEffect(() => {
  if (!user?.uid || !profileHydrated) return;
  if (pgDayMin < dailyGoal) { dailyCongratsPendingRef.current = false; return; }
  if (hasSeenDailyCongrats(user.uid)) return;

  dailyCongratsPendingRef.current = true;
}, [user?.uid, profileHydrated, pgDayMin, dailyGoal]);

// La célébration ne s’ouvre que lorsque l’accueil
// connecté est réellement affiché.
useEffect(() => {
  if (!connectedHomeActive) return;
  if (!user?.uid) return;
  if (!dailyCongratsPendingRef.current) return;
  if (progressRevealActiveRef.current) return;

  if (hasSeenDailyCongrats(user.uid)) {
    dailyCongratsPendingRef.current = false;
    return;
  }

  dailyCongratsPendingRef.current = false;
  markDailyCongratsSeen(user.uid);
  setShowDailyCongrats(true);
}, [connectedHomeActive, user?.uid, progressReveal, pgDayMin, dailyGoal]);

  const {
    lastBadge,
    resetLastBadge,
    currentStreak,
    longestStreak,
    activeDaysCount,
    unlockedBadges,
    markTodayActive,
  } = useStreak(user?.uid);

  const recordStreak = getRecordStreak(currentStreak, longestStreak);
  const storedStreakRewards = user?.streakRewards;

  useEffect(() => {
    if (!user?.uid) return;
    void syncStreakRewards(user.uid, recordStreak, storedStreakRewards);
  }, [user?.uid, recordStreak, storedStreakRewards]);

  const soundsRewardUnlocked = readUnlockedRewards({ streakRewards: storedStreakRewards }).sounds;

const unlockedNaturalSounds = useMemo(() => {
  if (!soundsRewardUnlocked) return orderedNaturalSounds.filter((sound) => !sound.streakDays);

  const favoriteSet = new Set(favoriteNatureSoundIds);
  const rewards = orderedNaturalSounds.filter((sound) => sound.streakDays);
  const favorites = orderedNaturalSounds.filter((sound) => !sound.streakDays && favoriteSet.has(sound.id));
  const others = orderedNaturalSounds.filter((sound) => !sound.streakDays && !favoriteSet.has(sound.id));

  return [...rewards, ...favorites, ...others];
}, [favoriteNatureSoundIds, orderedNaturalSounds, soundsRewardUnlocked]);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/auth/login")) setAuthMode("login");
    else if (path.includes("/auth/signup")) setAuthMode("signup");
  }, [location.pathname]);

const handleOnboardingComplete = async (answers) => {
  const uid = user?.uid;
  if (!uid || auth.currentUser?.uid !== uid) throw new Error("Connexion requise.");

  const normalized = normalizeOnboardingProfile(answers);

  if (!normalized.primaryNeed) {
    throw new Error("Choisissez votre objectif avant de continuer.");
  }

  const userRef = doc(db, "users", uid);

  await setDoc(
    userRef,
    {
      onboardingStatus: "completed",
      onboarding: {
        primaryNeed: normalized.primaryNeed,
        dailyTime: normalized.dailyTime,
        onboardingVersion: 3,
        completedAt: new Date().toISOString(),
      },
    },
    { merge: true },
  );

  if (auth.currentUser?.uid !== uid) return;
  setOnboardingProfile(normalized);

  writeHomeIntroTimestamp(homeIntroLastShownKey(user.uid));
  homeReturnIntroCheckedUidRef.current = user.uid;
  homeReturnIntroDeadlineRef.current = 0;
  setHomeReturnIntroPhase("done");
try {
  localStorage.removeItem("pendingSession");
} catch {
}

setShowOnboarding(false);
navigateStep("welcome");

window.requestAnimationFrame(() => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "auto",
  });

  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
});
};

const handleOnboardingProfileUpdate =
  useCallback(
    (updatedProfile) => {
      const normalized =
        normalizeOnboardingProfile({
          ...onboardingProfile,
          ...updatedProfile,
        });

      if (!normalized.primaryNeed) return;

      setOnboardingProfile(normalized);

      if (user?.uid) {

        const userRef = doc(
          db,
          "users",
          user.uid,
        );

        void (async () => {
          try {
            await setDoc(
              userRef,
              {
                onboarding: {
                  primaryNeed:
                    normalized.primaryNeed,
                  dailyTime:
                    normalized.dailyTime,
                  onboardingVersion: 3,
                  updatedAt:
                    new Date().toISOString(),
                },
              },
              { merge: true },
            );

          } catch (error) {
            console.warn(
              "Synchronisation du profil impossible :",
              error,
            );
          }
        })();
      }
    },
    [onboardingProfile, user?.uid],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stepFromUrl = params.get("step");

if (
  stepFromUrl === "welcome" ||
  stepFromUrl === "player"
) {
      setCurrentStep(stepFromUrl);
    } else {
      setCurrentStep("welcome");
    }
  }, [location.search]);


  // Scroll top on step change
  useEffect(() => {
    if (
      currentStep === "welcome" ||
      currentStep === "player"
    ) {
      const resetScroll = () => {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      };

      resetScroll(); // immédiat
      const r1 = requestAnimationFrame(() => {
        resetScroll();
        const r2 = requestAnimationFrame(resetScroll); // après peinture du nouveau contenu
        cleanupRef.current = r2;
      });

      return () => {
        cancelAnimationFrame(r1);
        if (cleanupRef.current) cancelAnimationFrame(cleanupRef.current);
      };
    }
  }, [currentStep]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const locationPathRef = useRef(location.pathname);
  useEffect(() => {
    locationPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    let profileTimer = null;
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      const epoch = ++authEpochRef.current;
      window.clearTimeout(profileTimer);
      setProfileError("");
      setShowOnboarding(false);
      setOnboardingProfile({ primaryNeed: "", dailyTime: "2min", onboardingVersion: 3 });
      homeReturnIntroCheckedUidRef.current = null;
      homeReturnIntroDeadlineRef.current = 0;
      setHomeReturnIntroPhase("idle");
      const needsOnboarding = signupIntentRef.current;
      signupIntentRef.current = false;
      tooltipManager.beginAuthSync();
      firestoreUnsubRef.current?.();
      firestoreUnsubRef.current = null;
      
if (!currentUser) {
  document.documentElement.setAttribute("data-theme", "light");

  tooltipManager.setUser(null, []);
  setUser(null);
  setIsPremium(false);
  setPremiumStatus("ready");
  setProfileHydrated(false);
  setPgDayMin(0);
  setPgWeekMin(0);

  try {
    localStorage.removeItem("currentUserId");
  } catch {}

  if (!isPublicPath(locationPathRef.current)) navigate("/", { replace: true });

  setIsLoading(false);
  return;
}

      setUser(currentUser);
      setIsPremium(false);
      setPremiumStatus("checking");
      setProfileHydrated(false);
      try { localStorage.setItem("currentUserId", currentUser.uid); } catch {}
      const pg = readProgress(currentUser.uid);
      setPgDayMin(pg.dayMin);
      setPgWeekMin(pg.weekMin);
      tooltipManager.setUser(currentUser.uid, []);

      const userRef = doc(db, "users", currentUser.uid);
      profileTimer = window.setTimeout(() => {
        if (authEpochRef.current === epoch) setProfileError("Votre profil n’a pas pu être chargé. Vérifiez votre connexion puis réessayez.");
      }, 12000);
      firestoreUnsubRef.current = onSnapshot(userRef, { includeMetadataChanges: true }, (snapshot) => {
        if (!isMountedRef.current || authEpochRef.current !== epoch || auth.currentUser?.uid !== currentUser.uid) return;
        if (snapshot.metadata.fromCache || snapshot.metadata.hasPendingWrites || !snapshot.exists()) return;
        window.clearTimeout(profileTimer);
        const data = snapshot.data();
        let legacyProfile = null;
        try { legacyProfile = JSON.parse(localStorage.getItem(`onboarding:${currentUser.uid}`) || "null"); } catch {}
        const onboardingState = resolveOnboardingState(data, legacyProfile);
        if (!normalizeOnboardingProfile(data.onboarding || {}).primaryNeed && normalizeOnboardingProfile(legacyProfile || {}).primaryNeed) {
          void updateDoc(userRef, { onboardingStatus: "completed", onboarding: { ...legacyProfile, onboardingVersion: 3, migratedAt: new Date().toISOString() } }).catch((error) => console.warn("Migration de l’onboarding impossible :", error));
        }
        setOnboardingProfile(onboardingState.profile);
        setShowOnboarding(onboardingState.required);
        setProfileError("");
        const nextPremium = data.isPremium === true && data.deletionPending !== true;
        setIsPremium((previous) => {
          if (!previous && nextPremium) setPremiumActivating(false);
          return nextPremium;
        });
        setUser({ ...currentUser, ...data, uid: currentUser.uid });
        setPremiumStatus("ready");
        setProfileHydrated(true);
        tooltipManager.setUser(currentUser.uid, data.guidesSeen?.tooltips || []);
      }, (error) => {
        if (!isMountedRef.current || authEpochRef.current !== epoch) return;
        console.warn("Erreur onSnapshot user:", error);
        setIsPremium(false);
        setPremiumStatus("error");
        window.clearTimeout(profileTimer);
        setProfileHydrated(false);
        setProfileError("Votre profil n’a pas pu être chargé. Réessayez dans quelques instants.");
      });

      void createUserProfile(currentUser, { onboardingStatus: needsOnboarding ? "pending" : "skipped" }).catch((error) => console.warn("Erreur création profil Firestore :", error));
      if (pendingNewsletterRef.current) {
        const pending = pendingNewsletterRef.current;
        void setDoc(userRef, pending, { merge: true }).then(() => {
          if (auth.currentUser?.uid === currentUser.uid && pendingNewsletterRef.current === pending) pendingNewsletterRef.current = null;
        }).catch((error) => console.warn("Préférence newsletter non enregistrée :", error));
      }
      if (locationPathRef.current.startsWith("/auth")) navigate("/", { replace: true });
      setIsLoading(false);
    });

    return () => {
      window.clearTimeout(profileTimer);
      authEpochRef.current += 1;
      unsubscribe();
      firestoreUnsubRef.current?.();
      firestoreUnsubRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileRetry]);

  useEffect(() => {
    if (isLoading) return;
    if (user) return;

    if (!isPublicPath(location.pathname)) {
      navigate("/", { replace: true });
    }
  }, [user, isLoading, location.pathname, navigate]);

useLayoutEffect(() => {
  const html = document.documentElement;
  const body = document.body;
  const path = location.pathname;
  const onPublicHome = path === "/" && !user;
  const onLegal = path.startsWith("/legal/");
  const legalFromProfile = onLegal && location.state?.legalOrigin === "profile";
  const onPublicLegal = onLegal && !legalFromProfile;
  const onPublicContent = !user && ["/apropos", "/frequences", "/science"].includes(path);
  const forceLight = onPublicHome || onPublicLegal || onPublicContent || path.startsWith("/blog") || path.startsWith("/auth") || path === "/forgot-password" || path === "/reset-password";

  body.classList.toggle("landing-page", onPublicHome || onPublicLegal);

  if (forceLight) {
    html.setAttribute("data-theme", "light");
  } else {
    applyStoredTheme();
  }

  return () => {
    body.classList.remove("landing-page");
  };
}, [location.pathname, location.state?.legalOrigin, user]);

  // Touch UX (no bounce)
  useEffect(() => {
    document.body.classList.add("no-bounce");
    return () => document.body.classList.remove("no-bounce");
  }, []);

  const refreshPremiumAccess = useCallback(async () => {
    if (!auth.currentUser?.uid) return false;
    setPremiumStatus("checking");
    setPaymentError("");
    try {
      const { data } = await httpsCallable(functions, "refreshPremiumStatus", { timeout: 20000 })();
      if (auth.currentUser?.uid) {
        setIsPremium(data?.isPremium === true);
        setPremiumStatus("ready");
      }
      return data?.isPremium === true;
    } catch (error) {
      console.warn("Vérification Premium impossible :", error);
      setPremiumStatus("error");
      setPaymentError("La confirmation du paiement prend plus de temps que prévu. Réessayez dans quelques instants.");
      return false;
    }
  }, []);

  // Stripe return param
  useEffect(() => {
    if (!isMountedRef.current) return;

    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get("payment");

    if (paymentStatus) {
      localStorage.setItem("stripePaymentStatus", paymentStatus);
      window.location.replace(getCanonicalOrigin());
    } else {
      const saved = localStorage.getItem("stripePaymentStatus");
      if (saved === "success") {
        // ✅ Banner discret au lieu d'un alert bloquant
        setPremiumActivating(true);
      }
      localStorage.removeItem("stripePaymentStatus");
    }
  }, []);

  useEffect(() => {
    if (isPremium && premiumActivating) {
      setPremiumActivating(false);
    }
  }, [isPremium, premiumActivating]);

  useEffect(() => {
    if (!premiumActivating || !user?.uid || premiumStatus === "checking" || premiumRefreshAttemptedRef.current === user.uid) return;
    premiumRefreshAttemptedRef.current = user.uid;
    void refreshPremiumAccess();
  }, [premiumActivating, premiumStatus, refreshPremiumAccess, user?.uid]);

  useEffect(() => {
    if (!user || !profileHydrated || profileError || showOnboarding || !isMountedRef.current) return;
    const raw = localStorage.getItem("pendingSession");
    if (!raw) return;
    localStorage.removeItem("pendingSession");
    try {
      const { soundId, durationKey } = JSON.parse(raw);
      const sound = soundsDatabase.find((s) => s.id === soundId);
      if (sound) handleSoundSelection(sound, durationKey);
    } catch {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, showOnboarding, profileHydrated, profileError]);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  const handleUpgradeToPremium = async (billingOverride = null) => {
    if (!isMountedRef.current) return;

if (!user) {
  const billingToRemember = billingOverride === "yearly" || billingOverride === "monthly" ? billingOverride : premiumBilling;

  try {
    localStorage.removeItem("directCheckout");
    localStorage.setItem("showPremiumAfterAuth", "true");
    localStorage.setItem("preferredBilling", billingToRemember);
  } catch {
    /* noop */
  }

  navigate("/auth/signup");
  return;
}

    setIsProcessingPayment(true);
    setPaymentError("");

    try {
      const createCheckoutSession = httpsCallable(
        functions,
        "createCheckoutSession",
      );
      const billingToUse = billingOverride || premiumBilling;
      const priceId =
        billingToUse === "yearly" ? STRIPE_PRICE_YEARLY : STRIPE_PRICE_MONTHLY;
      const { data } = await createCheckoutSession({
        priceId,
        origin: getCanonicalOrigin(),
      });

      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      if (!data?.sessionId) throw new Error("Session ID manquant");

      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({
        sessionId: data.sessionId,
      });
      if (error) throw error;
    } catch (err) {
      console.error("Erreur redirection Stripe:", err);
      if (isMountedRef.current)
        setPaymentError(err?.message || "Erreur lors de la redirection");
    } finally {
      if (isMountedRef.current) setIsProcessingPayment(false);
    }
  };

  const recordSessionCompletion = useCallback((listenedSeconds = 0) => {
    if (!user) return;
    const addMin = Math.max(0, Number(listenedSeconds) || 0) / 60;
    if (!addMin) return;

    const cur = readProgress(user.uid);
    const newDay = cur.dayMin + addMin;
    const newWeek = cur.weekMin + addMin;
    try { sessionStorage.setItem("harmonauria:progressReveal", JSON.stringify({ from: cur.dayMin, to: newDay, at: Date.now() })); } catch { /* noop */ }

    writeProgress(user.uid, {
      dayMin: newDay,
      daySessions: cur.daySessions + 1,
      weekMin: newWeek,
      weekSessions: cur.weekSessions + 1,
    });
    bumpSessionCount(user.uid);
    setPgDayMin(newDay);
    setPgWeekMin(newWeek);

    if (!isPremium && user?.uid) {
      consumeFreeMinutes(user.uid, addMin);
    }
  }, [user, isPremium]);

  const logCurrentListen = useCallback(
    async (reason = "finish", listenedSeconds = 0) => {
      if (!currentSound) return false;

      const dateISO = toISOParis(new Date());
      const trackId = currentTrackIdForLog(
        currentSound,
        selectedDuration,
        withMusic,
      );

      const minutes = Math.max(0, Number(listenedSeconds) || 0) / 60;

      if (minutes <= 0) return false;

      await addEntry({
        dateISO,
        type: "listen",
        minutes,
        content: currentSound?.name || "",
        meta: { trackId, reason },
      });

      return true;
    },
    [currentSound, selectedDuration, withMusic, addEntry],
  );

  const finalizeSession = useCallback(
    async (reason = "finish", listenedSeconds = 0) => {
      if (sessionLoggedRef.current || !currentSound) return;

      // ✅ verrou immédiat pour éviter tout double comptage si onFinalize repart
      sessionLoggedRef.current = true;

try {
  recordSessionCompletion(listenedSeconds);

  try {
    sessionStorage.setItem(
      "auria:afterglow",
      String(Date.now() + 10 * 60 * 1000),
    );
  } catch {
    /* noop */
  }

  const listenWasLogged = await logCurrentListen(reason, listenedSeconds);

        // ✅ la streak ne bouge qu'après une écoute réellement enregistrée
        if (listenWasLogged) {
          await markTodayActive();
        }
      } catch (error) {
        console.error("❌ Erreur finalisation session :", error);
      }
    },
    [
      currentSound,
      logCurrentListen,
      recordSessionCompletion,
      markTodayActive,
    ],
  );

  const finalizeAISession = useCallback(
    async (session, listenedSeconds = 0, reason = "finish") => {
      if (!user?.uid || !session) return;

      const minutes = Math.max(0, Number(listenedSeconds) || 0) / 60;
      const durationKey = session.context?.durationKey || "na";

      if (minutes <= 0) return;

      const dateISO = toISOParis(new Date());

      try {
        // ✅ Même compteur local que le player classique :
        // c’est lui qui déclenche DailyGoalCongratsToast via pgDayMin.
        const cur = readProgress(user.uid);
        const newDay = cur.dayMin + minutes;
        const newWeek = cur.weekMin + minutes;
        try { sessionStorage.setItem("harmonauria:progressReveal", JSON.stringify({ from: cur.dayMin, to: newDay, at: Date.now() })); } catch { /* noop */ }

        writeProgress(user.uid, {
          dayMin: newDay,
          daySessions: cur.daySessions + 1,
          weekMin: newWeek,
          weekSessions: cur.weekSessions + 1,
        });
        bumpSessionCount(user.uid);
setPgDayMin(newDay);
setPgWeekMin(newWeek);

try {
  sessionStorage.setItem(
    "auria:afterglow",
    String(Date.now() + 10 * 60 * 1000),
  );
} catch {
  /* noop */
}

await addEntry({
          dateISO,
          type: "listen",
          minutes,
          content: session.title || "Moment avec Auria",
          meta: {
            trackId: `ai:${session.context?.intent || "relax"}:${durationKey}`,
            reason: reason === "ended" ? "ai-finish" : `ai-${reason}`,
            sessionType: "ai-composed",
          },
        });
      } catch (error) {
        console.error("❌ Erreur finalisation session IA :", error);
      }
    },
    [user?.uid, addEntry],
  );

  const requireAuthGate = useCallback(
    (pending = null) => {
      if (pending) {
        try {
          localStorage.setItem("pendingSession", JSON.stringify(pending));
        } catch {
          /* noop */
        }
      }
navigate("/auth/signup", {
  state: {
    from: "respirations",
  },
});
    },
    [navigate],
  );

const openPremiumGate = useCallback(
  (from = null) => {
    if (!user) {
      try {
        localStorage.setItem(
          "showPremiumAfterAuth",
          "true",
        );

        localStorage.setItem(
          "preferredBilling",
          premiumBilling,
        );
      } catch {
        /* noop */
      }

      navigate("/auth/signup", {
        state: from
          ? {
              from,
            }
          : undefined,
      });

      return;
    }

    setShowPremiumModal(true);
  },
  [
    user,
    premiumBilling,
    navigate,
  ],
);

const resolveClassicLaunch = useCallback(
  (sound, durationKeyOverride) => {
    if (!user) {
      requireAuthGate({
        soundId: sound.id,
        durationKey: durationKeyOverride || "30sec",
      });

      return null;
    }

    if (PREMIUM_IDS_BASE.includes(sound.id) && !isPremium) {
      setShowPremiumModal(true);
      return null;
    }

    if (!isPremium && user?.uid && isInCooldown(user.uid)) {
      setShowFreeLimitModal(true);
      return null;
    }

    const durationKey = CLASSIC_DURATIONS.has(durationKeyOverride) ? durationKeyOverride : "30sec";
    const files = sound.files?.[durationKey] || sound.files?.["5min"];

    if (!files) {
      console.warn("Aucun fichier audio trouvé pour cette session");
      return null;
    }

    let resolvedWithMusic = withMusic;
    let nextSrc = resolvedWithMusic ? files.music : files.noMusic;

    if (!nextSrc) {
      resolvedWithMusic = !resolvedWithMusic;
      nextSrc = resolvedWithMusic ? files.music : files.noMusic;
    }

    if (!nextSrc) {
      console.warn("Aucune source audio exploitable");
      return null;
    }

    return {
      sound,
      durationKey,
      resolvedWithMusic,
      nextSrc,
      breathingSrc: files.breathing || "",
    };
  },
  [isPremium, requireAuthGate, user, withMusic],
);

const commitClassicLaunch = useCallback(
  (launch, { showTransition = true } = {}) => {
    if (!launch) return false;

    if (showTransition) {
      window.dispatchEvent(new Event("harmonauria:player-transition-start"));
    }

    setWithMusic(launch.resolvedWithMusic);
if (user?.uid) {
  void updateDoc(doc(db, "users", user.uid), {
    "uiPreferences.playerListeningMode": launch.resolvedWithMusic ? "music" : "noMusic",
  }).catch((error) => {
    console.warn("Impossible d’enregistrer le mode d’écoute.", error);
  });
}
    setSelectedDuration(launch.durationKey);
    setCurrentSound(launch.sound);
    setPlayerMainSrc(launch.nextSrc);
    setPlayerBreathingSrc(launch.breathingSrc);
    sessionLoggedRef.current = false;
    navigateStep("player");

    return true;
  },
  [navigateStep, user?.uid]
);

const handleSoundSelection = useCallback(
  (sound, durationKeyOverride) => {
    const launch = resolveClassicLaunch(sound, durationKeyOverride);
    return commitClassicLaunch(launch);
  },
  [commitClassicLaunch, resolveClassicLaunch],
);

  const handleAuth = async (e, authPayload = {}) => {
    e.preventDefault();
    if (!isMountedRef.current) return;

    setAuthError("");

    const cleanPseudo = cleanPseudoForProfile(authPayload.pseudo || pseudo);
    const wantsNewsletter =
      typeof authPayload.newsletterAccepted === "boolean"
        ? authPayload.newsletterAccepted
        : newsletterAccepted;

    if (authMode === "signup" && cleanPseudo.length < 2) {
      setAuthError("Choisissez un pseudo d’au moins 2 caractères");
      return;
    }

    setIsLoading(true);

    try {
      if (authMode === "signup") {
        signupIntentRef.current = true;
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );

        await updateProfile(userCredential.user, {
          displayName: cleanPseudo,
        });
        await createUserProfile(userCredential.user, { onboardingStatus: "pending" });

        await setDoc(
          doc(db, "users", userCredential.user.uid),
          {
            pseudo: cleanPseudo,
            displayName: cleanPseudo,
            email: userCredential.user.email,
            newsletterAccepted: wantsNewsletter,
            newsletterAcceptedAt: wantsNewsletter
              ? new Date().toISOString()
              : null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );

        pendingNewsletterRef.current = {
          pseudo: cleanPseudo,
          displayName: cleanPseudo,
          newsletterAccepted: wantsNewsletter,
          newsletterAcceptedAt: wantsNewsletter
            ? new Date().toISOString()
            : null,
          updatedAt: new Date().toISOString(),
        };

        setPseudo("");
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }

      // La redirection finale sera gérée par onAuthStateChanged
    } catch (error) {
      if (!isMountedRef.current) return;

      let errorMessage = "Une erreur est survenue";

      switch (error.code) {
        case "auth/user-not-found":
        case "auth/wrong-password":
        case "auth/invalid-credential":
          errorMessage = "Email ou mot de passe incorrect";
          break;
        case "auth/email-already-in-use":
          errorMessage = "Cet email est déjà utilisé";
          break;
        case "auth/weak-password":
          errorMessage = "Le mot de passe doit contenir au moins 6 caractères";
          break;
        case "auth/invalid-email":
          errorMessage = "Format d'email invalide";
          break;
        case "auth/network-request-failed":
          errorMessage =
            "Connexion impossible. Vérifiez votre réseau et réessayez.";
          break;
        case "auth/too-many-requests":
          errorMessage = "Trop de tentatives. Réessayez dans quelques minutes.";
          break;
        default:
          errorMessage = "Une erreur est survenue. Veuillez réessayer.";
      }

      setAuthError(errorMessage);
    } finally {
      signupIntentRef.current = false;
      if (isMountedRef.current) setIsLoading(false);
    }
  };

const handleLogout = async () => {
  try {
    await signOut(auth);

    document.documentElement.setAttribute("data-theme", "light");

    setCurrentStep("welcome");
    setShowPremiumModal(false);

    navigate("/", { replace: true });

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  } catch (error) {
    console.error("❌ Erreur déconnexion :", error);
  }
};

  useEffect(() => {
    if (!showPremiumModal) return;
    const pref = localStorage.getItem("preferredBilling");
    if (pref === "yearly" || pref === "monthly") {
      setPremiumBilling(pref);
    }
  }, [showPremiumModal]);

useEffect(() => {
  if (!user || !isMountedRef.current) return;

  const shouldShowPremium = localStorage.getItem("showPremiumAfterAuth");

  if (shouldShowPremium !== "true") return;

  if (isPremium) {
    localStorage.removeItem("showPremiumAfterAuth");
    return;
  }

  if (!onboardingProfile?.primaryNeed || showOnboarding) return;

  const preferredBilling = localStorage.getItem("preferredBilling");

  if (preferredBilling === "monthly" || preferredBilling === "yearly") {
    setPremiumBilling(preferredBilling);
  }

  setShowPremiumModal(true);
  localStorage.removeItem("showPremiumAfterAuth");
}, [user, isPremium, onboardingProfile?.primaryNeed, showOnboarding]);

const showBackground =
  !!user &&
  !isPublicDemo &&
  !location.pathname.startsWith("/auth") &&
  !isLegalPage;

  const homeReturnGreeting = useMemo(
    () => getHomeReturnGreeting(user, pseudo),
    // homeReturnIntroToken : force le recalcul de l'heure
    // quand l'onglet revient après une longue absence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, pseudo, homeReturnIntroToken],
  );

useLayoutEffect(() => {
  const html = document.documentElement;
  const body = document.body;

  if (!connectedHomeActive) return undefined;

  /*
    La topbar utilise une hauteur CSS fixe.
    Aucune mesure du DOM et aucun ResizeObserver :
    la géométrie de l'accueil ne change plus pendant le seuil.
  */
  html.style.setProperty(
    "--connected-home-top",
    "calc(var(--topbar-height, 56px) + env(safe-area-inset-top, 0px))",
  );

  html.classList.add("connected-home-fit");
  body.classList.add("connected-home-fit");

  return () => {
    html.classList.remove("connected-home-fit");
    body.classList.remove("connected-home-fit");
    html.style.removeProperty("--connected-home-top");
  };
}, [connectedHomeActive]);

useLayoutEffect(() => {
  const uid = user?.uid;
  if (!uid || !connectedHomeActive || homeReturnIntroCheckedUidRef.current === uid) return;
  homeReturnIntroCheckedUidRef.current = uid;
  if (!isFirstGreetingOfDay(readHomeIntroTimestamp(homeIntroLastShownKey(uid)))) {
    setHomeReturnIntroPhase("done");
    return;
  }
  homeReturnIntroDeadlineRef.current = Date.now() + HOME_INTRO_VISIBLE_MS + HOME_INTRO_LEAVE_MS;
  writeHomeIntroTimestamp(homeIntroLastShownKey(uid));
  setHomeReturnIntroPhase("visible");
}, [connectedHomeActive, user?.uid]);

useEffect(() => {
  if (!connectedHomeActive || !["visible", "leaving"].includes(homeReturnIntroPhase)) return undefined;
  const deadline = homeReturnIntroDeadlineRef.current;
  const remaining = deadline - Date.now();
  if (remaining <= 0) { setHomeReturnIntroPhase("done"); return undefined; }
  const timers = [window.setTimeout(() => setHomeReturnIntroPhase("done"), remaining)];
  if (homeReturnIntroPhase === "visible") timers.push(window.setTimeout(() => setHomeReturnIntroPhase("leaving"), Math.max(0, remaining - HOME_INTRO_LEAVE_MS)));
  return () => timers.forEach((timer) => window.clearTimeout(timer));
}, [connectedHomeActive, homeReturnIntroPhase, homeReturnIntroToken]);

useLayoutEffect(() => {
  if (!connectedHomeActive) return;

  let raw = null;
  try { raw = sessionStorage.getItem("harmonauria:progressReveal"); } catch { /* noop */ }
  if (!raw) return;
  try { sessionStorage.removeItem("harmonauria:progressReveal"); } catch { /* noop */ }

  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

  try {
    const data = JSON.parse(raw);
    const from = Math.max(0, Number(data.from) || 0);
    const to = Number(data.to) || 0;

    if (to <= from) return;
    if (Date.now() - (Number(data.at) || 0) > 10 * 60 * 1000) return;

    // Le badge est figé sur l'ancienne valeur dès la première peinture.
    progressRevealActiveRef.current = true;
    setProgressReveal({ from, to, phase: "arming" });

    const measure = () => {
      const badge = document.querySelector(".topbar-progress-badges__daily");
      const rect = badge?.getBoundingClientRect();
      const target = rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : { x: 130, y: 46 };

      const auriaNode = document.querySelector(".aic-home-ritual__presence") || document.querySelector(".aic-living-being") || document.querySelector(".aic-card");
      const auriaRect = auriaNode?.getBoundingClientRect();
      const origin = auriaRect ? { x: auriaRect.left + auriaRect.width / 2, y: auriaRect.top + auriaRect.height / 2 - auriaRect.height * AURIA_ORIGIN_OPTICAL_Y } : { x: window.innerWidth / 2, y: window.innerHeight * 0.58 };

      setProgressReveal((current) => (current?.phase === "arming" ? { ...current, target, origin, phase: "veil" } : current));
    };

    window.requestAnimationFrame(() => window.requestAnimationFrame(measure));
  } catch { /* noop */ }
}, [connectedHomeActive]);

useEffect(() => {
  if (!progressReveal) return undefined;

  if (progressReveal.phase === "arming") return undefined;

  const delay = progressReveal.phase === "veil" ? 1180 : 2000;

  const id = window.setTimeout(() => {
    if (progressReveal.phase === "fill") progressRevealActiveRef.current = false;
    setProgressReveal((current) => {
      if (!current) return current;
      return current.phase === "veil" ? { ...current, phase: "fill" } : null;
    });
  }, delay);

  return () => window.clearTimeout(id);
}, [progressReveal]);

const topbarDayMin = progressReveal && progressReveal.phase !== "fill" ? progressReveal.from : pgDayMin;

useEffect(() => {
  const uid = user?.uid;
  if (!uid || !connectedHomeActive) return undefined;
  const handleReturn = () => {
    if (document.visibilityState !== "visible") return;
    const deadline = homeReturnIntroDeadlineRef.current;
    if (deadline && Date.now() >= deadline) setHomeReturnIntroPhase("done");
    if (isFirstGreetingOfDay(readHomeIntroTimestamp(homeIntroLastShownKey(uid)))) {
      homeReturnIntroDeadlineRef.current = Date.now() + HOME_INTRO_VISIBLE_MS + HOME_INTRO_LEAVE_MS;
      writeHomeIntroTimestamp(homeIntroLastShownKey(uid));
      setHomeReturnIntroPhase("visible");
    }
    setHomeReturnIntroToken((token) => token + 1);
  };
  document.addEventListener("visibilitychange", handleReturn);
  window.addEventListener("pageshow", handleReturn);
  return () => {
    document.removeEventListener("visibilitychange", handleReturn);
    window.removeEventListener("pageshow", handleReturn);
  };
}, [connectedHomeActive, user?.uid]);

  const showHomeReturnIntro = connectedHomeActive && (homeReturnIntroPhase === "visible" || homeReturnIntroPhase === "leaving");

  /* La carte de palier attend l'accueil, la salutation, l'animation de
     progression et la célébration d'objectif avant de s'afficher. */
  const badgeCelebrationReady = connectedHomeActive && !showHomeReturnIntro && !progressReveal && !showDailyCongrats;


const unlockPlayerAudio = useCallback(async () => {
  try {
    await audioManager.unlock();
  } catch {
    /* Le player retentera au montage. */
  }
}, []);

const prepareAuriaSession = useCallback((payload) => {
  if (!payload?.session?.arc?.tracks?.[0]?.file) return false;

  try {
    void audioManager.unlock();
  } catch {}

  navigate("/ia/player", {
    state: { ...payload },
  });

  return true;
}, [navigate]);

const startClassicFromFlow = useCallback(({ sound, durationKey }) => {
  const launch = resolveClassicLaunch(sound, durationKey);

  if (!launch) return false;

  try {
    void audioManager.unlock();
  } catch {}

return commitClassicLaunch(launch, { showTransition: true });

}, [commitClassicLaunch, resolveClassicLaunch]);

const startMomentFromHome = useCallback(() => {
  navigate("/moment", { state: { from: "/" } });
}, [navigate]);

const openRefugeFromAuria = useCallback(() => {
  if (!isPremium) return;
  navigate("/refuge", { state: { from: "/" } });
}, [isPremium, navigate]);

const aiComposerHomeSection = (
  <div className="home-unified-entry">
    <AIComposerCard
      uid={user?.uid}
      isPremium={isPremium}
      preferredIntent={effectivePrimaryNeed}
      onStartMoment={startMomentFromHome}
      onOpenRefuge={openRefugeFromAuria}
      onLockedClick={() => openPremiumGate("auria-refuge")}
      chipReady={badgeCelebrationReady}
    />
  </div>
);

  // Attente résolution Firebase Auth (évite la page blanche au démarrage)
  if (user && profileError) {
    return (
      <main
        role="alert"
        style={{ minHeight: "100dvh", display: "grid", placeContent: "center", gap: 16, padding: 24, textAlign: "center" }}
      >
        <p>{profileError}</p>
        <button
          type="button"
          onClick={() => { setProfileError(""); setProfileHydrated(false); setProfileRetry((value) => value + 1); }}
        >Réessayer</button>
        <button
          type="button"
          onClick={() => { void signOut(auth).catch(() => setProfileError("La déconnexion a échoué. Réessayez.")); }}
        >Se déconnecter</button>
      </main>
    );
  }
  if (isLoading || (user && !profileHydrated)) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            border: "3px solid #e2e8f0",
            borderTopColor: "#76A5A7",
            animation: "spin 0.7s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <>
        <ScrollToTop />

        {playerTransitionVisible && <div className="player-transition-cover" aria-hidden="true" />}

        {showHomeReturnIntro && (
          <div
            className={`home-return-intro ${
              homeReturnIntroPhase === "leaving" ? "is-leaving" : ""
            }`}
            role="status"
            aria-live="polite"
            aria-label={homeReturnGreeting}
          >
            <div className="home-return-intro__content">
              <img
                src="/refuge-symbol.svg"
                alt=""
                aria-hidden="true"
                width={64}
                height={64}
                decoding="sync"
                className="home-return-intro__logo"
              />
              <p>{homeReturnGreeting}</p>
            </div>
          </div>
        )}
{progressReveal?.origin && (
  <div
    className={`progress-reveal ${progressReveal.phase === "fill" ? "is-leaving" : ""}`}
    style={{ "--reveal-x": `${progressReveal.target.x}px`, "--reveal-y": `${progressReveal.target.y}px`, "--reveal-from-x": `${progressReveal.origin.x}px`, "--reveal-from-y": `${progressReveal.origin.y}px` }}
    aria-hidden="true"
  >
    <div className="progress-reveal__veil" />
    <div className="progress-reveal__pulse progress-reveal__pulse--1" />
    <div className="progress-reveal__pulse progress-reveal__pulse--2" />
    <div className="progress-reveal__pulse progress-reveal__pulse--3" />
    <div className="progress-reveal__orb-track">
      <div className="progress-reveal__orb" />
    </div>
  </div>
)}
        {showDailyCongrats && (
          <DailyGoalCongratsToast
            goalLabel={formatDurationLabelFromMinutes(dailyGoal)}
            onClose={() => setShowDailyCongrats(false)}
          />
        )}

        {showBackground && (
          <BackgroundLayer
            images={imageFiles}
            desktopImages={desktopImageFiles}
            index={bgIndex}
            disabled={isBlogArticlePage}
          />
        )}

        {premiumActivating && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 10000,
              background: "linear-gradient(90deg, #4f6f68, #61837a)",
              color: "#fff",
              textAlign: "center",
              padding: "12px 16px",
              fontSize: 14,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              boxShadow: "0 2px 16px rgba(79, 111, 104, 0.32)",
              animation: "slideDown .3s ease-out both",
            }}
          >
            <span
              style={{
                animation: "spin 1s linear infinite",
                display: "inline-block",
              }}
            >
              ⏳
            </span>
            Votre Refuge Personnel s'active… L'accès s'ouvre automatiquement.
            {paymentError && <button type="button" onClick={() => { premiumRefreshAttemptedRef.current = user?.uid || null; void refreshPremiumAccess(); }}>Réessayer</button>}
            <style>{`
      @keyframes slideDown {
        from { transform: translateY(-100%); opacity: 0; }
        to   { transform: translateY(0);    opacity: 1; }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
      }
    `}</style>
          </div>
        )}

        <div className="app-shell">
          <style>{`
          .topbar-title {
            color: #39696B;
            font-size: 1.2rem;
            font-weight: 600;
            letter-spacing: -0.2px;
          }
          button:focus-visible, input[type="range"]:focus-visible {
            outline: 2px solid #76A5A7; outline-offset: 2px;
          }
          button:focus:not(:focus-visible), input[type="range"]:focus:not(:focus-visible) {
            outline: none; box-shadow: none;
          }
          button, a, [role="button"] { -webkit-tap-highlight-color: transparent; }
button[data-selected="true"]:not(.objective-card):not(.duration-card) { border-color: #76A5A7 !important; background-color: #eff6ff !important; color: #61837A !important; }
button.objective-card[data-selected="true"], button.duration-card[data-selected="true"] {
  border-color: #76A5A7 !important;
  background-color: #ffffff !important;
  box-shadow: 0 12px 28px rgba(118, 165, 167, 0.28), inset 0 1px 0 rgba(118, 165, 167, 0.12) !important;
  transform: translateY(-4px);
}
          button:active { filter: none !important; }
          @media (forced-colors: active) { button[data-selected="true"] { forced-color-adjust: none; } }
        `}</style>

          {/* ===== Premium Modal ===== */}
          {showPremiumModal && (
            <div
              id="premium-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="premium-title"
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 9999,
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                padding: "24px 12px",
                paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
                overscrollBehavior: "contain",
                background: "rgba(15, 23, 42, 0.22)",
                backdropFilter: "blur(5px)",
                WebkitBackdropFilter: "blur(5px)",
              }}
              onClick={(e) => {
                if (e.target === e.currentTarget) setShowPremiumModal(false);
              }}
            >
              <div
                className="premium-card"
                role="document"
                aria-describedby="premium-desc"
              >
                <div className="premium-card__glow" aria-hidden="true" />

                {/* === CLOSE BUTTON === */}
<button
  type="button"
  onClick={() => setShowPremiumModal(false)}
  className="premium-close"
  aria-label="Fermer"
>
✕
</button>

<div className="premium-badge">
  <span className="premium-badge__label">Le Refuge Personnel</span>
</div>

                {/* === HEADLINE === */}
<div className="premium-header">
  <h2 id="premium-title" className="premium-title">Un Refuge qui évolue avec vous</h2>
  <p id="premium-desc" className="premium-subtitle">Des musiques inédites composées à la main, de nouvelles créations chaque mois, et la liberté d’y ajouter une respiration guidée selon votre moment.</p>
</div>
                {/* === BLOC PRIX + CTA (collés) === */}
                <div className="premium-buy-block">
                  {/* Toggle billing */}
                  <div
                    className="premium-billing-toggle"
                    role="tablist"
                    aria-label="Choisir la facturation"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={premiumBilling === "monthly"}
                      className={`premium-billing-btn ${premiumBilling === "monthly" ? "active" : ""}`}
                      onClick={() => setPremiumBilling("monthly")}
                    >
                      Mensuel
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={premiumBilling === "yearly"}
                      className={`premium-billing-btn ${premiumBilling === "yearly" ? "active" : ""}`}
                      onClick={() => setPremiumBilling("yearly")}
                    >
                      Annuel
                      <span className="premium-billing-chip">-28%</span>
                    </button>
                  </div>

                  <div className="premium-pricing-inline">
                    {premiumBilling === "monthly" ? (
                      <>
                        <div className="price-line">
                          <span className="price">6,99€</span>
                          <span
                            style={{
                              fontSize: 18,
                              fontWeight: 700,
                              color: "rgba(255,255,255,0.6)",
                              marginLeft: 4,
                            }}
                          >
                            /mois
                          </span>
                        </div>
                        <div className="premium-pricing__equivalent">Sans engagement</div>
                        <div className="premium-pricing__tag">{trialEligible ? "7 jours offerts, puis 6,99€/mois · Résiliable à tout moment" : "6,99€/mois · Résiliable à tout moment"}</div>
                      </>
                    ) : (
<>
  <div className="price-line">
    <span className="price">59,88€</span>
    <span
      style={{
        fontSize: 18,
        fontWeight: 700,
        color: "rgba(255,255,255,0.6)",
        marginLeft: 4,
      }}
    >
      /an
    </span>
  </div>

<div className="premium-pricing__equivalent premium-pricing__equivalent--yearly">soit 4,99€ par mois</div>

  <div className="premium-pricing__tag">
    {trialEligible ? "7 jours offerts, puis 59,88€/an · Résiliable à tout moment" : "59,88€/an · Résiliable à tout moment"}
  </div>
</>
                    )}
                  </div>

                  <button
                    onClick={() => handleUpgradeToPremium()}
                    disabled={isProcessingPayment}
                    className="premium-cta"
                    aria-label={trialEligible ? "Commencer mes 7 jours offerts" : "Ouvrir mon Refuge Personnel"}
                  >
                    {isProcessingPayment ? "Redirection…" : trialEligible ? "Commencer — 7 jours offerts" : "Ouvrir mon Refuge Personnel"}
                  </button>

                  <div className="premium-reassurance">{trialEligible ? "Aucun prélèvement avant 7 jours · Annulable en 1 clic" : "Annulable en 1 clic"}</div>

                  {!!paymentError && (
                    <div className="premium-error" role="alert">
                      {paymentError}
                    </div>
                  )}
                </div>
                {/* === TABLEAU COMPARATIF === */}
                <div className="premium-compare">
                  <div className="compare-col compare-col--free">
                    <div className="compare-col__label">Découverte</div>
                    <ul>
                      <li className="compare-item compare-item--no">
                        Sessions jusqu'à 2 minutes
                      </li>
                      <li className="compare-item compare-item--no">
                        6 respirations guidées
                      </li>
                      <li className="compare-item compare-item--no">
                        8 paysages sonores
                      </li>
                      <li className="compare-item compare-item--no">
                        1 guide offert
                      </li>
                    </ul>
                  </div>
                  <div className="compare-col compare-col--premium">
                    <div className="compare-col__label">Refuge Personnel</div>
<ul>
  <li className="compare-item compare-item--yes">🎼 Musiques inédites composées à la main</li>
  <li className="compare-item compare-item--yes">De nouvelles créations ajoutées chaque mois</li>
  <li className="compare-item compare-item--yes">Votre accompagnement personnel</li>
  <li className="compare-item compare-item--yes">Les 9 respirations guidées, à ajouter selon vos envies</li>
  <li className="compare-item compare-item--yes">Sessions jusqu'à 10 minutes</li>
  <li className="compare-item compare-item--yes">16 paysages sonores</li>
  <li className="compare-item compare-item--yes">Les guides du Refuge et tous les fonds d'écran</li>
</ul>
                  </div>
                </div>

                <button
                  onClick={() => setShowPremiumModal(false)}
                  className="premium-cancel"
                >
                  Continuer en découverte
                </button>
              </div>
            </div>
          )}

{showFreeLimitModal && user?.uid && (
  <BodyPortal>
    <FreeLimitModal
      uid={user.uid}
      onClose={() => setShowFreeLimitModal(false)}
    />
  </BodyPortal>
)}

          {/* ===== Routes publiques ===== */}
          <Routes>
            <Route path="/reset-password" element={<Resetpassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
<Route
  path="/science"
  element={
    <Science
      showThemeDropdown={!!user}
      isPremium={isPremium}
      longestStreak={recordStreak}
    />
  }
/>
            <Route
              path="/auth/:mode"
              element={
                <AuthPage
                  onAuth={handleAuth}
                  authMode={authMode}
                  setAuthMode={setAuthMode}
                  email={email}
                  setEmail={setEmail}
                  password={password}
                  setPassword={setPassword}
                  authError={authError}
                  isLoading={isLoading}
                  onBack={() => navigate("/")}
                  onNewsletterChange={(val) => setNewsletterAccepted(val)}
                  pseudo={pseudo}
                  setPseudo={setPseudo}
                />
              }
            />
            <Route path="/legal/cgv" element={<CGV />} />
            <Route
              path="/legal/mentions-legales"
              element={<MentionsLegales />}
            />
            <Route
              path="/legal/politique-confidentialite"
              element={<PolitiqueConfidentialite />}
            />
            <Route path="/legal/remboursement" element={<Remboursement />} />
            <Route path="*" element={null} />
          </Routes>

          {user && isPremium && showAuriaNatureOnboarding && !showOnboarding && (
            <AuriaNatureOnboarding
              uid={user.uid}
              naturalSounds={unlockedNaturalSounds}
              onDismiss={() => setShowAuriaNatureOnboarding(false)}
              onComplete={(ids) => {
                setAuriaPreferences((current) => ({
                  ...(current || {}),
                  favoriteNatureSoundIds: ids,
                  onboarding: {
                    ...(current?.onboarding || {}),
                    natureCompletedAt: new Date().toISOString(),
                  },
                }));
                setShowAuriaNatureOnboarding(false);
              }}
            />
          )}

          {/* ===== App privée ===== */}
          <Routes>
            <Route
              path="/"
              element={
                user && showOnboarding ? (
                  <UserOnboarding onComplete={handleOnboardingComplete} />
                ) : (
<div className={`app-fill ${user ? "app-safe--no-top" : ""}`}>

{user &&
  !isLegalPage &&
  currentStep === "welcome" &&
  (
<div className="header-floating app-header--compact app-header--home-main">
      <TopbarLeftOnly
        user={user}
        currentStreak={currentStreak}
        longestStreak={longestStreak}
        unlockedBadges={unlockedBadges}
        activeDaysCount={activeDaysCount}
        dailyProgressMin={topbarDayMin}
        progressPhase={progressReveal?.phase || ""}
        dailyTime={effectiveDailyTime}
        onOpenProgress={() =>
          navigate("/progression", {
            state: { from: "/" },
          })
        }
        isPremium={isPremium}
        suspendTooltips={showHomeReturnIntro || showPremiumModal}
      />
    </div>
  )}

                    {currentStep === "welcome" && (
                      <div className={`app-fill ${user ? "app-safe--no-top" : "app-fill--public-home"}`}>
                        <div className={`welcome-container welcome-container--accueil ${user ? "" : "welcome-container--public-home"}`}>
                          <div className={`welcome-card cq-hero ${user ? "welcome-card--connected-home" : "welcome-card--public-home"}`}>
                            {user ? aiComposerHomeSection : (
                              <PublicHome
                                onConnect={() => openPublicAuth("login")}
                                onSignup={() => openPublicAuth("signup")}
                                onStartDemo={startPublicDemo}
                                onSubscribe={startPremiumSignup}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ===== PLAYER ===== */}
{currentStep === "player" && currentSound && (
  <PlayerPage
    user={isPublicDemo ? null : user}
    sound={currentSound}
    withMusic={isPublicDemo ? true : withMusic}
    mainSrc={playerMainSrc}
    breathingSrc={playerBreathingSrc}
    selectedDuration={selectedDuration}
    isPremium={isPublicDemo ? false : isPremium}
    longestStreak={isPublicDemo ? 0 : recordStreak}
    naturalSounds={isPublicDemo ? PUBLIC_DEMO_NATURAL_SOUNDS : unlockedNaturalSounds}
    demoMode={isPublicDemo}
    initialNaturalSoundId={isPublicDemo ? "mer" : null}
    initialNaturalSoundVolume={0.6}
    onDemoSignup={() => continueAfterPublicDemo("signup")}
    onDemoLogin={() => continueAfterPublicDemo("login")}
    onOpenPremium={isPublicDemo ? undefined : () => setShowPremiumModal(true)}
onMusicModeChange={isPublicDemo ? undefined : (nextWithMusic) => {
  setWithMusic(nextWithMusic);

  if (!user?.uid) return;

  void updateDoc(doc(db, "users", user.uid), {
    "uiPreferences.playerListeningMode": nextWithMusic ? "music" : "noMusic",
  }).catch((error) => {
    console.warn("Impossible d’enregistrer le mode d’écoute.", error);
  });
}}
    onBack={isPublicDemo ? exitPublicDemo : returnToHomeAfterPlayer}
    onFinish={isPublicDemo ? exitPublicDemo : returnToHomeAfterPlayer}
    onSaveNote={isPublicDemo ? undefined : (content) => addEntry({
      dateISO: toISOParis(new Date()),
      type: "ressenti",
      minutes: 0,
      content,
      meta: {
        source: "player-end",
        sessionTitle: currentSound?.name || "Session de respiration",
        trackId: currentTrackIdForLog(currentSound, selectedDuration, withMusic),
      },
    })}
    onFinalize={isPublicDemo ? undefined : (reason, listenedSeconds) => {
      void finalizeSession(reason, listenedSeconds);
    }}
  />
)}
                  </div>
                )
              }
            />

            {/* === AUTRES ROUTES === */}
            <Route
              path="/moment"
              element={
                <BreathingPage
                  sounds={soundsDatabase}
                  user={user}
                  isPremium={isPremium}
                  premiumSoundIds={PREMIUM_IDS_BASE}
                  preferredIntent={effectivePrimaryNeed}
                  dailyTime={effectiveDailyTime}
                  longestStreak={longestStreak}
                  onLockedDuration={() => openPremiumGate("breathing-duration")}
                  onLockedSound={() => openPremiumGate("breathing-sound")}
                  onStart={startClassicFromFlow}
                />
              }
            />
            <Route
              path="/refuge"
              element={
                <PremiumRoute user={user} status={premiumStatus} isPremium={isPremium} onRetry={refreshPremiumAccess}>
                <RefugePage
                  user={user}
                  isPremium={isPremium}
                  preferredIntent={effectivePrimaryNeed}
                  naturalSounds={unlockedNaturalSounds}
                  favoriteNatureSoundIds={favoriteNatureSoundIds}
                  currentStreak={currentStreak}
                  onBeforePlayerLaunch={unlockPlayerAudio}
                  onPrepareAuriaSession={prepareAuriaSession}
                />
                </PremiumRoute>
              }
            />
            <Route
              path="/respirer"
              element={
                <Navigate
                  to="/moment"
                  replace
                  state={{
                    ...(location.state || {}),
                    momentFlow: location.state?.momentFlow || {
                      entry: "custom",
                      step: "intent",
                      intentKey: "",
                      durationKey: "",
                      selectedSoundId: null,
                    },
                  }}
                />
              }
            />
            <Route path="/ressenti" element={<RessentiPage user={user} />} />
            <Route
              path="/calendrier"
              element={
                <CalendarPage
                  isPremium={isPremium}
                  longestStreak={longestStreak}
                />
              }
            />
            <Route
              path="/progression"
              element={
                <ProgressPage
                  uid={user?.uid}
                  dailyTime={effectiveDailyTime}
                  primaryNeed={effectivePrimaryNeed}
                  dailyProgressMin={pgDayMin}
                  weeklyProgressMin={pgWeekMin}
                  currentStreak={currentStreak}
                  longestStreak={longestStreak}
                  isPremium={isPremium}
                />
              }
            />
<Route path="/decouvrir" element={<Navigate to="/apropos" replace />} />
<Route
  path="/frequences"
  element={
    <FrequencesPage
      showThemeDropdown={!!user}
      isPremium={isPremium}
      longestStreak={recordStreak}
    />
  }
/>

<Route
  path="/apropos"
  element={
    <Apropos
      showThemeDropdown={!!user}
      isPremium={isPremium}
      longestStreak={recordStreak}
    />
  }
/>
<Route
  path="/guides"
  element={
    <Guides
      user={user}
      isPremium={isPremium}
      longestStreak={recordStreak}
      openPremium={openPremiumGate}
    />
  }
/>
            <Route path="/blog" element={<Blog showThemeDropdown={!!user} />} />
            <Route path="/blog/:slug" element={<BlogArticle showThemeDropdown={!!user} />} />
            <Route
              path="/profil"
              element={
<ProfilePage
  user={user}
  isPremium={isPremium}
  longestStreak={recordStreak}
  onLogout={handleLogout}
  onboardingProfile={onboardingProfile}
  naturalSounds={unlockedNaturalSounds}
  onUpdateOnboarding={handleOnboardingProfileUpdate}
/>
              }
            />
            <Route path="/breathing-export" element={<BreathingExportPage />} />
            <Route path="/visualizer" element={<AuriaVisualizer />} />
            <Route
              path="/ia"
              element={
                <Navigate
                  to="/moment"
                  replace
                  state={{
                    ...(location.state || {}),
                    momentFlow: {
                      entry: "custom",
                      step: "intent",
                      intentKey: "",
                      durationKey: "",
                      selectedSoundId: null,
                    },
                  }}
                />
              }
            />

            <Route
              path="/ia/player"
              element={
<PremiumRoute user={user} status={premiumStatus} isPremium={isPremium} onRetry={refreshPremiumAccess}>
<AIPlayerPage
  user={user}
  naturalSounds={unlockedNaturalSounds}
  isPremium={isPremium}
  longestStreak={recordStreak}
  markTodayActive={markTodayActive}
  onSaveNote={(
    content,
    completedSession,
    listenedSeconds,
  ) =>
    addEntry({
      dateISO: toISOParis(new Date()),
      type: "ressenti",
      minutes: 0,
      content: content.trim(),
      meta: {
        source: "auria-player-end",
        sessionType: "ai-composed",
        sessionId: completedSession?.id || "",
        sessionTitle:
          completedSession?.title ||
          "Session Auria",
        listenedSeconds:
          Math.round(
            Number(listenedSeconds) || 0,
          ),
        trackId: `ai:${
          completedSession?.context?.intent ||
          "relax"
        }:${
          completedSession?.context
            ?.durationKey || "na"
        }`,
      },
    })
  }
  onFinalize={finalizeAISession}
/>
</PremiumRoute>
              }
            />
            <Route path="*" element={null} />
          </Routes>
          {user && (
            <BadgeCelebration badge={badgeCelebrationReady ? lastBadge : null} isPremium={isPremium} onClose={resetLastBadge} />
          )}
        </div>
      </>
    </HelmetProvider>
  );
}

export default App;
